import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 border-primary/50 hover:border-primary/70 shadow-professional-sm hover:shadow-professional-md observer-button-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 border-destructive/50 hover:border-destructive/70 shadow-professional-sm",
        outline:
          "border-border bg-card text-foreground hover:bg-muted hover:text-accent-foreground hover:border-accent shadow-professional-sm observer-button-outline",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border-secondary/60 hover:border-secondary shadow-professional-sm",
        ghost: "border-transparent bg-card/80 hover:bg-muted hover:text-accent-foreground hover:border-accent/50 shadow-professional-sm",
        link: "border-transparent text-primary underline-offset-4 hover:underline",
        success: "bg-success text-success-foreground hover:bg-success/90 border-success/50 hover:border-success/70 shadow-professional-sm",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 border-warning/50 hover:border-warning/70 shadow-professional-sm",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
